import React, { useEffect, useMemo, useState } from 'react'
import backBtnMobile from "../../assets/images/backBtnMobile.svg";
import noImageFound from "../../assets/images/noImageFound.svg";
import noVideoFound from "../../assets/images/noVideoFound.svg";
import nodataclaim from "../../assets/images/nodataclaim.svg";
import nodatafaq from "../../assets/images/nodatafaq.svg";
import backBtn from "../../assets/images/backBtn.svg";
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EducationalImage, EducationalVideo, EducationalFAQs, EducationalPdfViewer } from './EducationalComponents';
import EducationalContentHeader from './EducationalContentHeader';
import EducationalContentContinueButton from './EducationalContentContinueButton';
import { useDispatch, useSelector } from 'react-redux';
import { getMaterialHandler } from '../../redux/action/guestUserAction/getMaterial';
import { stepperNextStepHandler, stepperNextStepSliceAction } from '../../redux/action/guestUserAction/stepperNextStep';
import { getItem } from '../../common/localStorage';
import { STATUS_CODE } from "../../common";
import {
	GUEST_EDUCATIONAL_BACK_DEFAULT,
	GUEST_EDUCATIONAL_BACK_STORAGE_KEY,
	GUEST_ENROLLMENT_PATHS,
	guestEnrollmentPathFromStep,
} from '../../common/guestEnrollmentFlow';
import NoDataAvailableState from "./NoDataAvailableState";
import Loader from "../Loder/loader";

const EducationalContent = () => {
	const navigate = useNavigate()
	const location = useLocation();
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const getCurrentStepsSelector = useSelector((state) => state?.stepperNextStep);
	const campaignMemberId = getItem('guestLoginResponse')?.id;
	const [currentIndex, setCurrentIndex] = useState(0);
	const getMaterialSelector = useSelector((state) => state?.getMaterial);
	const isMaterialApiSuccess =
		getMaterialSelector?.data?.statusCode === STATUS_CODE.OK;
	const documentList = useMemo(
		() => getMaterialSelector?.data?.data?.documentList || [],
		[getMaterialSelector?.data?.data?.documentList],
	);
	const showEmptyMaterialState =
		!getMaterialSelector?.isLoading &&
		isMaterialApiSuccess &&
		documentList.length === 0;
	const showNoDataState =
		!getMaterialSelector?.isLoading &&
		!isMaterialApiSuccess;
	const eduLastStepKey = useMemo(() => {
		const idPart = campaignMemberId ? `:${campaignMemberId}` : "";
		return `guestEdu:lastIndex${idPart}`;
	}, [campaignMemberId]);
	const eduRestoreLastKey = "guestEdu:restoreLastOnBack";
	useEffect(() => {
		if (!campaignMemberId) return;
		dispatch(
			getMaterialHandler({
				campaignMemberId: campaignMemberId,
			}),
		);
	}, [campaignMemberId, dispatch]);

	// Paycheck → educational uses navigate(path) without state; keep the logical "back" target.
	useEffect(() => {
		const fromState = location.state?.guestEducationalBackTo;
		if (!fromState || typeof fromState !== "string") return;
		try {
			window.sessionStorage?.setItem(GUEST_EDUCATIONAL_BACK_STORAGE_KEY, fromState);
		} catch {
			// ignore
		}
	}, [location.state?.guestEducationalBackTo]);

	const { imageList, videoList, pdfList, faqItems } = useMemo(() => {
		const pickSignedUrl = (doc) => doc?.signedUrl || doc?.fileUrl || "";
		const normalizeType = (type) => (type || "").toString().toUpperCase();

		const images = documentList.filter((d) => normalizeType(d?.type) === "IMAGE");
		const videos = documentList.filter((d) => normalizeType(d?.type) === "VIDEO");
		const pdfs = documentList.filter((d) => normalizeType(d?.type) === "PDF");
		const faqDoc = documentList.find((d) => normalizeType(d?.type) === "FAQ");

		return {
			imageList: images.map(pickSignedUrl).filter(Boolean),
			videoList: videos.map(pickSignedUrl).filter(Boolean),
			pdfList: pdfs.map(pickSignedUrl).filter(Boolean),
			faqItems: faqDoc?.faqItem || [],
		};
	}, [documentList]);

	const stepsData = useMemo(() => {
		const steps = [];
		imageList.forEach((url) => {
			steps.push({ title: t("Educational Content"), type: "image", url });
		});
		videoList.forEach((url) => {
			steps.push({ title: t("Educational Content"), type: "video", url });
		});
		pdfList.forEach((url) => {
			steps.push({ title: t("Educational Content"), type: "pdf", url });
		});
		if (faqItems?.length) steps.push({ title: t("Educational Content"), type: "faqs" });

		const total = steps.length;
		return steps.map((s, idx) => ({
			...s,
			stepText: `${t("Step")} 1 ${t("of")} 4`,
			progress: `25%`,
			buttonText: idx === total - 1 ? "Next" : "Next",
		}));
	}, [faqItems?.length, imageList, pdfList, t, videoList]);

	useEffect(() => {
		if (currentIndex > stepsData.length - 1) setCurrentIndex(0);
	}, [currentIndex, stepsData.length]);

	// If user navigates back from `/guest-user/paycheck`, start on the last content step.
	useEffect(() => {
		if (!stepsData?.length) return;
		try {
			const shouldRestore = window.sessionStorage?.getItem(eduRestoreLastKey);
			if (!shouldRestore) return;

			const savedIndexRaw = window.sessionStorage?.getItem(eduLastStepKey);
			const savedIndex = Number.isFinite(Number(savedIndexRaw))
				? Number(savedIndexRaw)
				: stepsData.length - 1;

			const clamped = Math.max(0, Math.min(stepsData.length - 1, savedIndex));
			setCurrentIndex(clamped);
			window.sessionStorage?.removeItem(eduRestoreLastKey);
		} catch {
			// ignore storage errors (private mode, blocked, etc.)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stepsData?.length]);

	// Persist last seen step so we can restore after browser back.
	useEffect(() => {
		try {
			window.sessionStorage?.setItem(eduLastStepKey, String(currentIndex));
		} catch {
			// ignore storage errors
		}
	}, [currentIndex, eduLastStepKey]);

	const currentStepData = stepsData[currentIndex] || stepsData[0];
	const stepHeaderTitle = currentStepData?.title || t("Educational Content");

	const handleBack = () => {
		if (currentIndex > 0) {
			setCurrentIndex(currentIndex - 1);
		} else {
			let backTo = location.state?.guestEducationalBackTo;
			if (!backTo) {
				try {
					backTo = window.sessionStorage?.getItem(GUEST_EDUCATIONAL_BACK_STORAGE_KEY);
				} catch {
					backTo = undefined;
				}
			}
			navigate(backTo || GUEST_EDUCATIONAL_BACK_DEFAULT);
		}
	};

	const goToNextEnrollmentStep = () => {
		dispatch(
			stepperNextStepHandler({
				currentStep: "EDUCATIONAL_CONTENT_VIEWED",
			}),
		);
	};

	const handleContinue = () => {
		if (currentIndex < stepsData.length - 1) {
			setCurrentIndex(currentIndex + 1);
		} else {
			goToNextEnrollmentStep();
			// Mark that we should reopen this page at the last step when user browser-backs from paycheck.
			try {
				window.sessionStorage?.setItem(eduRestoreLastKey, "1");
				window.sessionStorage?.setItem(eduLastStepKey, String(stepsData.length - 1));
			} catch {
				// ignore storage errors
			}
		}
	};

	const handleEmptyMaterialContinue = () => {
		goToNextEnrollmentStep();
	};

	const renderEmptyState = (message, imageSrc) => (
		<div className="mobileMargin noData noMobileMargin">
			<div>
				<img src={imageSrc} alt="" />
				<div className="d-flex flex-column align-items-center justify-content-center mt-24 mb-4">
					<h3 className="fs-20 fw-700 colorblur">{t(message)}</h3>
					<p className="fs-16">{t("There is no educational material found")}</p>
				</div>
			</div>
		</div>
	);

	const renderStepContent = () => {
		switch (currentStepData.type) {
			case 'image':
				return imageList.length > 0 && currentStepData.url
					? <EducationalImage key={currentStepData.url} imageUrl={currentStepData.url} />
					: renderEmptyState("No image found", noImageFound);
			case 'video':
				return videoList.length > 0 && currentStepData.url
					? <EducationalVideo key={currentStepData.url} videoUrl={currentStepData.url} />
					: renderEmptyState("No video found", noVideoFound);
			case 'pdf':
				return pdfList.length > 0 && currentStepData.url
					? <EducationalPdfViewer key={currentStepData.url} pdfUrl={currentStepData.url} />
					: renderEmptyState("No PDF found", nodataclaim);
			case 'faqs':
				return faqItems?.length
					? <EducationalFAQs faqs={faqItems} />
					: renderEmptyState("No FAQs found", nodatafaq);
			default:
				return null;
		}
	};

	useEffect(() => {
		const payload = getCurrentStepsSelector?.data;
		if (payload?.statusCode === STATUS_CODE.OK) {
			const nextStep = payload?.data?.nextStep;
			const nextPath = guestEnrollmentPathFromStep(nextStep);
			const shouldSetYesDecision =
				nextStep === "ENROLLMENT_ESIGN_VIEWED" ||
				nextStep === "ENROLLMENT_ESIGN_COMPLETED";
			const shouldSetNoDecision = nextStep === "ENROLLMENT_DECLINED";
			navigate(nextPath || GUEST_ENROLLMENT_PATHS.educational, {
				state: {
					guestEducationalBackTo: GUEST_ENROLLMENT_PATHS.welcome,
					...(shouldSetYesDecision ? { enrollmentDecision: "YES" } : {}),
					...(shouldSetNoDecision ? { enrollmentDecision: "No" } : {}),
				},
			});
			dispatch(stepperNextStepSliceAction.stepperNextStepSliceReset());
		}
	}, [dispatch, getCurrentStepsSelector, navigate]);

	return (
		<EducationalContentHeader
			backBtnSrc={backBtn}
			backBtnMobileSrc={backBtnMobile}
			onBack={handleBack}
			title={stepHeaderTitle}
			hideBackButton={currentIndex === 0}
			hideNavigation={currentStepData?.hideNavigation}
			stepText={currentStepData?.stepText || `${t("Step")} 1 ${t("of")} 4`}
			progress={currentStepData?.progress || "25%"}
		>
			<div className="stepperview mb-60">
				{getMaterialSelector?.isLoading
					? <Loader />
					: showNoDataState
						? <NoDataAvailableState subtitle="There is no educational material found" />
						: showEmptyMaterialState
							? <NoDataAvailableState subtitle="There is no educational material found" />
							: renderStepContent()}
			</div>
			{(isMaterialApiSuccess || showEmptyMaterialState) && (
				<EducationalContentContinueButton
					hideNavigation={currentStepData?.hideNavigation}
					onContinue={showEmptyMaterialState ? handleEmptyMaterialContinue : handleContinue}
					buttonLabel={
						showEmptyMaterialState
							? t("Continue")
							: currentStepData?.buttonText
								? t(currentStepData.buttonText)
								: t("Continue")
					}
				/>
			)}
		</EducationalContentHeader>
	)
}

export default EducationalContent
